/**
 * Slice 9 — Settings + Team (SIMPLE).
 * Hospitable · Buy list (21c) · Team · Invite sheet.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ExternalLink, Loader2, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  useCreateMemberInvite,
  useRevokeMemberInvite,
  sendMemberInviteEmail,
  removePropertyAccess,
  memberInviteStatus,
  usePropertyAccessAndInvites,
  setPropertyTeamPermission,
  updateMemberInvitePermissions,
} from "@/hooks/useMemberInvites";
import {
  usePropertyCohostAccess,
  useCreateCohostInvite,
  cohostInviteStatus,
  removeCohostAccess,
  deleteCohostInvite,
  updateCohostMemberPermissions,
  updateCohostInvitePermissions,
} from "@/hooks/useCohostInvites";
import { useProperties } from "@/hooks/useProperties";
import { CleanerLanguageToggle } from "@/components/cleaner/CleanerLanguageToggle";
import {
  useCleanerAppLanguages,
  useSetCleanerAppLanguage,
  type CleanerAppLanguage,
} from "@/hooks/useCleanerAppLanguage";
import HospitableConnectCard from "@/components/owner/HospitableConnectCard";
import DeletePropertySection from "@/components/owner/DeletePropertySection";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUpdateProperty, type PropertyAccessInstructions } from "@/hooks/useProperties";
import { isAmazonBuyListUrl } from "@/lib/amazonBuyList";
import {
  TEAM_PERMISSION_MODULES,
  TEAM_PERMISSION_LABELS,
  TEAM_PERMISSION_SUBLABELS,
  accessLevelFromPermissions,
  isAllPermissionsSelected,
  resolveTeamPermissions,
  toggleTeamPermission,
  type TeamPermissionModule,
} from "@/lib/teamPermissions";
import { canManageTeamAccess } from "@/lib/propertyTabs";
import { usePropertyContext } from "@/contexts/PropertyContext";

interface SettingsTabProps {
  propertyId: string;
  property: {
    name: string;
    address: string | null;
    unit?: string | null;
    city?: string | null;
    state_province?: string | null;
    postal_code?: string | null;
    notes?: string | null;
    created_at: string;
    airbnb_ical_url: string | null;
    vrbo_ical_url: string | null;
    booking_ical_url: string | null;
    qr_code_url: string | null;
    property_code: string | null;
    status?: string;
    archived_at?: string | null;
    owner_id?: string;
    hospitable_property_id?: string | null;
    amazon_buy_list_url?: string | null;
  } & Partial<PropertyAccessInstructions>;
  isOwner?: boolean;
  accessLevel?: "full" | "limited";
}

type Role = "Cleaner" | "Team";
type AccessScope = "This property" | "All properties" | "Select properties";

const ACCESS_SCOPE_OPTIONS: AccessScope[] = [
  "This property",
  "All properties",
  "Select properties",
];

type TeamRow = {
  key: string;
  name: string;
  /** Profile id when this row is a cleaner whose language can be set. */
  userId?: string | null;
  /** Shown under name when different from display name (so emails stay visible). */
  email?: string | null;
  sub: string;
  /** Accepted role column. Pending rows use the PENDING pill instead. */
  roleLabel?: "Cleaner" | "Team";
  pending: boolean;
  initial: string;
  avatarUrl?: string | null;
  buttons: { label: "Remove" | "Resend" | "Cancel" | "Edit" | "Make Team" | "Make Cleaner"; onClick: () => void }[];
};

type EditTarget =
  | { kind: "cohost_member"; id: string; name: string; permissions: unknown }
  | { kind: "cohost_invite"; id: string; name: string; permissions: unknown }
  | { kind: "member_invite"; id: string; name: string; permissions: unknown };

function inviteUrl(token: string) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/invite?token=${token}`;
}

async function copyInviteLink(token: string): Promise<string> {
  const url = inviteUrl(token);
  try {
    await navigator.clipboard.writeText(url);
  } catch {
    /* clipboard may be blocked; still return url for toast */
  }
  return url;
}

/** Invite row exists; email may or may not have sent. Never use destructive red for the copy-link path. */
function toastInviteOutcome(ok: boolean, emailError?: string) {
  if (ok) {
    toast({ title: "Invite sent" });
    return;
  }
  toast({
    title: "Invite ready — link copied",
    description: emailError
      ? `Email didn’t send (${emailError}). Paste the link to share.`
      : "Email didn’t send. Paste the link to share.",
  });
}

async function invokeHospitableStatus(): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke("hospitable-proxy", {
    body: { action: "status" },
  });
  if (error) return false;
  return Boolean(data?.connected);
}

const SettingsTab = ({
  propertyId,
  property,
  isOwner = true,
  accessLevel = "full",
}: SettingsTabProps) => {
  const isMobile = useIsMobile();
  const { user, profile } = useAuth();
  const ctx = usePropertyContext();
  const ownerId = property.owner_id ?? user?.id ?? undefined;
  const canManage =
    isOwner ||
    canManageTeamAccess(
      ctx?.role ?? (isOwner ? "owner" : "cohost"),
      accessLevel,
      ctx?.permissions ?? null
    );

  const { data: accessAndInvites, refetch: refetchAccess } = usePropertyAccessAndInvites(
    propertyId,
    ownerId,
  );
  const { data: cohostAccess, refetch: refetchCohost } = usePropertyCohostAccess(propertyId);
  const createInvite = useCreateMemberInvite();
  const createCohostInvite = useCreateCohostInvite();
  const revokeInvite = useRevokeMemberInvite();
  const { data: ownedProperties = [] } = useProperties();

  const emailPropertyRow = useCallback(
    (id: string, fallbackName?: string | null) => {
      const owned = ownedProperties.find((p) => p.id === id) as
        | { name?: string | null; property_photo_url?: string | null; property_photo_urls?: string[] | null }
        | undefined;
      const current =
        id === propertyId
          ? (property as {
              name?: string | null;
              property_photo_url?: string | null;
              property_photo_urls?: string[] | null;
            })
          : null;
      const photo =
        owned?.property_photo_url?.trim() ||
        owned?.property_photo_urls?.[0]?.trim() ||
        current?.property_photo_url?.trim() ||
        current?.property_photo_urls?.[0]?.trim() ||
        null;
      return {
        name: (owned?.name || current?.name || fallbackName || "Property").trim() || "Property",
        photo_url: photo,
      };
    },
    [ownedProperties, property, propertyId],
  );

  const [hospAccountConnected, setHospAccountConnected] = useState(false);
  const [hospLoading, setHospLoading] = useState(true);
  const [hospOpen, setHospOpen] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("Cleaner");
  const [accessScope, setAccessScope] = useState<AccessScope>("This property");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [teamPermissions, setTeamPermissions] = useState<TeamPermissionModule[]>([]);
  const [permError, setPermError] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [editPermissions, setEditPermissions] = useState<TeamPermissionModule[]>([]);
  const [editPermError, setEditPermError] = useState(false);
  const [editBusy, setEditBusy] = useState(false);

  const openEditPermissions = (target: EditTarget) => {
    setEditTarget(target);
    setEditPermissions(resolveTeamPermissions(target.permissions));
    setEditPermError(false);
  };

  const closeEditPermissions = () => {
    setEditTarget(null);
    setEditPermissions([]);
    setEditPermError(false);
  };

  const handleSaveEditPermissions = async () => {
    if (!editTarget || !canManage) return;
    if (editPermissions.length === 0) {
      setEditPermError(true);
      return;
    }
    setEditBusy(true);
    try {
      if (editTarget.kind === "cohost_member") {
        await updateCohostMemberPermissions(editTarget.id, editPermissions);
      } else if (editTarget.kind === "cohost_invite") {
        await updateCohostInvitePermissions(editTarget.id, editPermissions);
      } else {
        await updateMemberInvitePermissions(editTarget.id, editPermissions);
      }
      toast({ title: "Permissions updated" });
      await refetchAccess();
      await refetchCohost();
      closeEditPermissions();
    } catch (err) {
      toast({
        title: "Could not update",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setEditBusy(false);
    }
  };

  const updateProperty = useUpdateProperty();
  const savedBuyUrl = (property.amazon_buy_list_url || "").trim();
  const [buyUrl, setBuyUrl] = useState(savedBuyUrl);
  const [buyError, setBuyError] = useState(false);

  useEffect(() => {
    setBuyUrl(savedBuyUrl);
    setBuyError(false);
  }, [savedBuyUrl]);

  const buyIsSaved = Boolean(savedBuyUrl) && savedBuyUrl === buyUrl.trim();
  const buyCanSave = canManage && buyUrl.trim().length > 0 && !buyIsSaved && !updateProperty.isPending;

  const saveBuyList = async () => {
    if (!canManage || !buyUrl.trim()) return;
    if (!isAmazonBuyListUrl(buyUrl)) {
      setBuyError(true);
      return;
    }
    try {
      await updateProperty.mutateAsync({
        propertyId,
        amazon_buy_list_url: buyUrl.trim(),
      });
      setBuyError(false);
      toast({ title: "Saved" });
    } catch (e) {
      toast({
        title: "Couldn’t save",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    }
  };

  const linked = Boolean(String(property.hospitable_property_id || "").trim());

  const refreshHosp = useCallback(async () => {
    setHospLoading(true);
    try {
      setHospAccountConnected(await invokeHospitableStatus());
    } finally {
      setHospLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshHosp();
  }, [refreshHosp]);

  const hospConnected = linked || hospAccountConnected;
  const hospStatus = linked
    ? "Connected"
    : hospAccountConnected
      ? "Connected · pick unit"
      : "Not connected";

  const switchRole = useCallback(
    async (targetEmail: string, next: "cleaner" | "team") => {
      if (!targetEmail) {
        toast({ title: "Missing email", variant: "destructive" });
        return;
      }
      try {
        await setPropertyTeamPermission(propertyId, targetEmail, next === "team" ? "full" : "cleaner");
        toast({ title: next === "team" ? "Switched to Team" : "Switched to Cleaner" });
        await Promise.all([refetchAccess(), refetchCohost()]);
      } catch (err) {
        toast({
          title: "Could not switch",
          description: err instanceof Error ? err.message : "Try again",
          variant: "destructive",
        });
      }
    },
    [propertyId, refetchAccess, refetchCohost],
  );

  const teamRows: TeamRow[] = useMemo(() => {
    const rows: TeamRow[] = [];
    const members = accessAndInvites?.members ?? [];
    const invites = accessAndInvites?.invites ?? [];
    const cohostMembers = cohostAccess?.members ?? [];
    const cohostInvites = cohostAccess?.invites ?? [];

    for (const m of cohostMembers) {
      const name =
        m.display_name?.trim() ||
        [m.first_name, m.last_name].filter(Boolean).join(" ").trim() ||
        m.grantee_email ||
        "Team";
      const email = m.grantee_email || "";
      rows.push({
        key: `cohost-m-${m.id}`,
        name,
        email: email || null,
        sub: "",
        roleLabel: "Team",
        pending: false,
        initial: name.slice(0, 1).toUpperCase(),
        avatarUrl: m.avatar_url?.trim() || null,
        buttons: [
          ...(email
            ? [
                {
                  label: "Make Cleaner" as const,
                  onClick: () => {
                    void switchRole(email, "cleaner");
                  },
                },
              ]
            : []),
          {
            label: "Edit",
            onClick: () =>
              openEditPermissions({
                kind: "cohost_member",
                id: m.id,
                name,
                permissions: m.permissions,
              }),
          },
          {
            label: "Remove",
            onClick: () => {
              void (async () => {
                try {
                  await removeCohostAccess(propertyId, email, m.cohost_user_id);
                  toast({ title: "Removed", description: "They no longer have access to your properties." });
                  await Promise.all([refetchCohost(), refetchAccess()]);
                } catch (err) {
                  toast({
                    title: "Could not remove",
                    description: err instanceof Error ? err.message : "Try again",
                    variant: "destructive",
                  });
                }
              })();
            },
          },
        ],
      });
    }

    for (const m of members) {
      const email = (m.grantee_email || "").toLowerCase();
      if (cohostMembers.some((c) => (c.grantee_email || "").toLowerCase() === email && email)) {
        continue;
      }
      const name =
        m.display_name?.trim() ||
        [m.first_name, m.last_name].filter(Boolean).join(" ").trim() ||
        m.grantee_email ||
        "Member";
      const isCleaner = m.role === "cleaner";
      rows.push({
        key: `pa-${m.id}`,
        name,
        userId: isCleaner ? m.grantee_user_id : null,
        email: email || null,
        sub: "",
        roleLabel: isCleaner ? "Cleaner" : "Team",
        pending: false,
        initial: name.slice(0, 1).toUpperCase(),
        avatarUrl: (m as { avatar_url?: string | null }).avatar_url?.trim() || null,
        buttons: [
          ...(email && isCleaner
            ? [
                {
                  label: "Make Team" as const,
                  onClick: () => {
                    void switchRole(email, "team");
                  },
                },
              ]
            : []),
          {
            label: "Remove",
            onClick: () => {
              void (async () => {
                try {
                  if (!email) throw new Error("Missing email");
                  await removePropertyAccess(propertyId, email);
                  toast({ title: "Removed", description: "They no longer have access to your properties." });
                  await Promise.all([refetchAccess(), refetchCohost()]);
                } catch (err) {
                  toast({
                    title: "Could not remove",
                    description: err instanceof Error ? err.message : "Try again",
                    variant: "destructive",
                  });
                }
              })();
            },
          },
        ],
      });
    }

    for (const inv of cohostInvites) {
      if (cohostInviteStatus(inv) !== "pending") continue;
      const name = inv.invited_email;
      rows.push({
        key: `cohost-i-${inv.id}`,
        name,
        sub: "Invited as Team",
        pending: true,
        initial: name.slice(0, 1).toUpperCase(),
        buttons: [
          {
            label: "Edit",
            onClick: () =>
              openEditPermissions({
                kind: "cohost_invite",
                id: inv.id,
                name,
                permissions: inv.permissions,
              }),
          },
          {
            label: "Resend",
            onClick: () => {
              void (async () => {
                try {
                  const sendResult = await sendMemberInviteEmail({
                    email: inv.invited_email,
                    token: inv.token,
                    invite_role: "company_admin",
                    owner_name: profile?.full_name ?? "Property owner",
                    property_name: property.name ?? "",
                    properties: [emailPropertyRow(propertyId, property.name)],
                    property_id: propertyId,
                    permission_label: "Team",
                  });
                  await copyInviteLink(inv.token);
                  if (sendResult.ok) toast({ title: "Resent" });
                  else
                    toast({
                      title: "Invite ready — link copied",
                      description: sendResult.error
                        ? `Email didn’t send (${sendResult.error}). Paste the link to share.`
                        : "Email didn’t send. Paste the link to share.",
                    });
                } catch (err) {
                  toast({
                    title: "Could not resend",
                    description: err instanceof Error ? err.message : "Try again",
                    variant: "destructive",
                  });
                }
              })();
            },
          },
          {
            label: "Cancel",
            onClick: () => {
              void (async () => {
                try {
                  await deleteCohostInvite(inv.id);
                  toast({ title: "Invite cancelled" });
                  await refetchCohost();
                } catch (err) {
                  toast({
                    title: "Could not cancel",
                    description: err instanceof Error ? err.message : "Try again",
                    variant: "destructive",
                  });
                }
              })();
            },
          },
        ],
      });
    }

    for (const inv of invites) {
      const status = memberInviteStatus(inv);
      if (status !== "pending") continue;
      const isCleaner = inv.invite_role === "cleaner";
      const name = inv.email;
      rows.push({
        key: `mi-${inv.id}`,
        name,
        sub: isCleaner ? "Invited as Cleaner" : "Invited as Team",
        pending: true,
        initial: name.slice(0, 1).toUpperCase(),
        buttons: [
          ...(isCleaner
            ? []
            : [
                {
                  label: "Edit" as const,
                  onClick: () =>
                    openEditPermissions({
                      kind: "member_invite",
                      id: inv.id,
                      name,
                      permissions: inv.permissions,
                    }),
                },
              ]),
          {
            label: "Resend",
            onClick: () => {
              void (async () => {
                try {
                  const resendRows =
                    Array.isArray(inv.target_property_ids) && inv.target_property_ids.length > 0
                      ? inv.target_property_ids.map((id) => emailPropertyRow(id))
                      : isCleaner
                        ? [emailPropertyRow(inv.property_id ?? propertyId, property.name)]
                        : ownedProperties.map((p) => emailPropertyRow(p.id, p.name));
                  const sendResult = await sendMemberInviteEmail({
                    email: inv.email,
                    token: inv.token,
                    invite_role: inv.invite_role,
                    owner_name: profile?.full_name ?? "Property owner",
                    property_name:
                      resendRows.length === 1
                        ? resendRows[0]!.name
                        : resendRows.length > 1
                          ? `${resendRows.length} properties`
                          : property.name ?? "",
                    properties: resendRows,
                    property_id: propertyId,
                    permission_label: isCleaner ? "Cleaner" : "Team",
                  });
                  await copyInviteLink(inv.token);
                  if (sendResult.ok) toast({ title: "Resent" });
                  else
                    toast({
                      title: "Invite ready — link copied",
                      description: sendResult.error
                        ? `Email didn’t send (${sendResult.error}). Paste the link to share.`
                        : "Email didn’t send. Paste the link to share.",
                    });
                } catch (err) {
                  toast({
                    title: "Could not resend",
                    description: err instanceof Error ? err.message : "Try again",
                    variant: "destructive",
                  });
                }
              })();
            },
          },
          {
            label: "Cancel",
            onClick: () => {
              void (async () => {
                try {
                  await revokeInvite.mutateAsync(inv.id);
                  toast({ title: "Invite cancelled" });
                  await refetchAccess();
                } catch (err) {
                  toast({
                    title: "Could not cancel",
                    description: err instanceof Error ? err.message : "Try again",
                    variant: "destructive",
                  });
                }
              })();
            },
          },
        ],
      });
    }

    return rows;
  }, [
    accessAndInvites,
    cohostAccess,
    emailPropertyRow,
    ownedProperties,
    property.name,
    propertyId,
    profile?.full_name,
    refetchAccess,
    refetchCohost,
    revokeInvite,
    switchRole,
  ]);

  const cleanerUserIds = useMemo(
    () => teamRows.map((row) => row.userId).filter((id): id is string => Boolean(id)),
    [teamRows],
  );
  const { data: cleanerLanguages = {} } = useCleanerAppLanguages(cleanerUserIds);
  const setCleanerLanguage = useSetCleanerAppLanguage();

  const changeCleanerLanguage = async (userId: string, language: CleanerAppLanguage) => {
    try {
      await setCleanerLanguage.mutateAsync({ userId, language });
      toast({
        title: language === "es" ? "Cleaner language set to Spanish" : "Cleaner language set to English",
        description: "They’ll see it the next time they open the app.",
      });
    } catch (err) {
      toast({
        title: "Couldn’t update language",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    }
  };

  const closeInvite = () => {
    setInviteOpen(false);
    setEmail("");
    setRole("Cleaner");
    setAccessScope("This property");
    setSelectedPropertyIds([]);
    setTeamPermissions([]);
    setPermError(false);
  };

  const toggleSelectedProperty = (id: string) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSend = async () => {
    if (!canManage || !ownerId) return;
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes("@")) {
      toast({ title: "Enter email", variant: "destructive" });
      return;
    }
    if (role === "Team" && teamPermissions.length === 0) {
      setPermError(true);
      return;
    }
    if (accessScope === "Select properties" && selectedPropertyIds.length === 0) {
      toast({ title: "Pick at least one property", variant: "destructive" });
      return;
    }
    setPermError(false);
    setBusy(true);
    try {
      if (role === "Cleaner") {
        const cleanerPropertyIds =
          accessScope === "All properties"
            ? ownedProperties.map((p) => p.id)
            : accessScope === "Select properties"
              ? selectedPropertyIds
              : [propertyId];
        const useMulti = cleanerPropertyIds.length > 1;
        const result = await createInvite.mutateAsync({
          ownerId,
          email: normalized,
          inviteRole: "cleaner",
          propertyId: useMulti ? propertyId : cleanerPropertyIds[0] ?? propertyId,
          expiresDays: 7,
          accessLevel: "limited",
          propertyIds: useMulti ? cleanerPropertyIds : null,
        });
        const cleanerRows = cleanerPropertyIds.map((id) => emailPropertyRow(id));
        const sendResult = await sendMemberInviteEmail({
          email: result.email,
          token: result.token,
          invite_role: "cleaner",
          owner_name: profile?.full_name ?? "Property owner",
          property_name:
            cleanerRows.length === 1
              ? cleanerRows[0]!.name
              : cleanerRows.length > 1
                ? `${cleanerRows.length} properties`
                : property.name ?? "",
          properties: cleanerRows,
          property_id: propertyId,
          permission_label: "Cleaner",
        });
        await copyInviteLink(result.token);
        toastInviteOutcome(sendResult.ok, sendResult.error);
      } else if (
        accessScope === "This property" ||
        (accessScope === "Select properties" &&
          selectedPropertyIds.length === 1 &&
          selectedPropertyIds[0] === propertyId)
      ) {
        const access = accessLevelFromPermissions(teamPermissions);
        const result = await createCohostInvite.mutateAsync({
          propertyId,
          email: normalized,
          accessLevel: access,
          permissions: teamPermissions,
        });
        const sendResult = await sendMemberInviteEmail({
          email: result.invited_email,
          token: result.token,
          invite_role: "company_admin",
          owner_name: profile?.full_name ?? "Property owner",
          property_name: property.name ?? "",
          properties: [emailPropertyRow(propertyId, property.name)],
          property_id: propertyId,
          permission_label: "Team",
        });
        await copyInviteLink(result.token);
        toastInviteOutcome(sendResult.ok, sendResult.error);
      } else if (accessScope === "Select properties") {
        const access = accessLevelFromPermissions(teamPermissions);
        // Single other property → cohost on that property; multi → scoped member invite
        if (selectedPropertyIds.length === 1) {
          const onlyId = selectedPropertyIds[0]!;
          const result = await createCohostInvite.mutateAsync({
            propertyId: onlyId,
            email: normalized,
            accessLevel: access,
            permissions: teamPermissions,
          });
          const row = emailPropertyRow(onlyId, property.name);
          const sendResult = await sendMemberInviteEmail({
            email: result.invited_email,
            token: result.token,
            invite_role: "company_admin",
            owner_name: profile?.full_name ?? "Property owner",
            property_name: row.name,
            properties: [row],
            property_id: onlyId,
            permission_label: "Team",
          });
          await copyInviteLink(result.token);
          toastInviteOutcome(sendResult.ok, sendResult.error);
        } else {
          const result = await createInvite.mutateAsync({
            ownerId,
            email: normalized,
            inviteRole: "company_admin",
            propertyId,
            expiresDays: 7,
            accessLevel: access,
            permissions: teamPermissions,
            propertyIds: selectedPropertyIds,
          });
          const selectedRows = selectedPropertyIds.map((id) => emailPropertyRow(id));
          const sendResult = await sendMemberInviteEmail({
            email: result.email,
            token: result.token,
            invite_role: "company_admin",
            owner_name: profile?.full_name ?? "Property owner",
            property_name:
              selectedRows.length === 1
                ? selectedRows[0]!.name
                : selectedRows.length > 1
                  ? `${selectedRows.length} properties`
                  : property.name ?? "",
            properties: selectedRows,
            property_id: propertyId,
            permission_label: "Team",
          });
          await copyInviteLink(result.token);
          toastInviteOutcome(sendResult.ok, sendResult.error);
        }
      } else {
        const access = accessLevelFromPermissions(teamPermissions);
        const result = await createInvite.mutateAsync({
          ownerId,
          email: normalized,
          inviteRole: "company_admin",
          propertyId,
          expiresDays: 7,
          accessLevel: access,
          permissions: teamPermissions,
        });
        const allRows = ownedProperties.map((p) => emailPropertyRow(p.id, p.name));
        const sendResult = await sendMemberInviteEmail({
          email: result.email,
          token: result.token,
          invite_role: "company_admin",
          owner_name: profile?.full_name ?? "Property owner",
          property_name:
            allRows.length === 1
              ? allRows[0]!.name
              : allRows.length > 1
                ? `${allRows.length} properties`
                : property.name ?? "",
          properties: allRows,
          property_id: propertyId,
          permission_label: "Team",
        });
        await copyInviteLink(result.token);
        toastInviteOutcome(sendResult.ok, sendResult.error);
      }
      await refetchAccess();
      await refetchCohost();
      closeInvite();
    } catch (err) {
      toast({
        title: "Could not send",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const allPermsOn = isAllPermissionsSelected(teamPermissions);
  const canSend =
    !busy &&
    email.trim().includes("@") &&
    (accessScope !== "Select properties" || selectedPropertyIds.length > 0) &&
    (role === "Cleaner" || teamPermissions.length > 0);

  const inviteBody = (
    <div className="flex flex-col gap-[22px]">
      <div className="flex flex-col gap-2">
        <div className="text-[13px] font-bold text-[#64748b]">Email</div>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@email.com"
          type="email"
          className="rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-4 py-3.5 text-base font-medium outline-none focus:border-[#24a89a] focus:bg-white"
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-[13px] font-bold text-[#64748b]">Role</div>
        <div className="flex gap-2">
          {(["Cleaner", "Team"] as Role[]).map((r) => {
            const on = role === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => {
                  setRole(r);
                  setPermError(false);
                  if (r === "Cleaner") {
                    setTeamPermissions([]);
                    setAccessScope("This property");
                  }
                }}
                className={cn(
                  "flex-1 rounded-[13px] border-[1.5px] py-[13px] text-center text-base",
                  on
                    ? "border-[#24a89a] bg-[#e2eeed] font-bold text-[#15776d]"
                    : "border-[#e6edf1] font-medium text-[#475569]",
                )}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-[13px] font-bold text-[#64748b]">Access</div>
        <div className="flex flex-col gap-2">
          {ACCESS_SCOPE_OPTIONS.map((a) => {
            const on = accessScope === a;
            return (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setAccessScope(a);
                  if (a === "Select properties" && selectedPropertyIds.length === 0) {
                    setSelectedPropertyIds(propertyId ? [propertyId] : []);
                  }
                }}
                className={cn(
                  "flex items-center gap-3 rounded-[13px] border-[1.5px] px-[15px] py-[13px] text-left",
                  on
                    ? "border-[#24a89a] bg-[#e2eeed] text-[#15776d]"
                    : "border-[#e6edf1] text-[#475569]",
                )}
              >
                <div
                  className={cn(
                    "h-[18px] w-[18px] shrink-0 rounded-md",
                    on ? "bg-[#24a89a]" : "border-[1.5px] border-[#cdd8e0]",
                  )}
                />
                <span className={cn("text-base", on ? "font-bold" : "font-medium")}>{a}</span>
              </button>
            );
          })}
        </div>
        {accessScope === "Select properties" ? (
          <div className="mt-1 max-h-[220px] overflow-y-auto rounded-[13px] border-[1.5px] border-[#e6edf1] bg-white">
            {ownedProperties.length === 0 ? (
              <p className="px-4 py-3 text-sm text-[#64748b]">No properties yet</p>
            ) : (
              ownedProperties.map((p) => {
                const on = selectedPropertyIds.includes(p.id);
                const photo = (p as { property_photo_url?: string | null }).property_photo_url?.trim() || "";
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleSelectedProperty(p.id)}
                    className={cn(
                      "flex w-full items-center gap-3 border-b border-[#eef2f5] px-3 py-2.5 text-left last:border-b-0",
                      on ? "bg-[#f3faf9]" : "bg-white",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-md",
                        on ? "bg-[#24a89a]" : "border-[1.5px] border-[#cdd8e0]",
                      )}
                    >
                      {on ? <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} /> : null}
                    </div>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[#eef2f5]">
                      {photo ? (
                        <img
                          src={photo}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <Building2 className="h-4 w-4 text-[#94a3b8]" />
                      )}
                    </div>
                    <span
                      className={cn(
                        "min-w-0 flex-1 truncate text-[15px]",
                        on ? "font-bold text-[#15776d]" : "font-medium text-[#334155]",
                      )}
                    >
                      {p.name}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        ) : null}
      </div>
      {role === "Team" ? (
        <div className="flex flex-col gap-2">
          <div className="text-[13px] font-bold text-[#64748b]">Permissions</div>
          <div className="flex flex-wrap gap-2">
            {TEAM_PERMISSION_MODULES.map((mod) => {
              const on = teamPermissions.includes(mod);
              return (
                <button
                  key={mod}
                  type="button"
                  onClick={() => {
                    setTeamPermissions((prev) => toggleTeamPermission(prev, mod));
                    setPermError(false);
                  }}
                  className={cn(
                    "flex min-h-12 flex-col justify-center rounded-[13px] border-[1.5px] px-4",
                    on
                      ? "border-[#24a89a] bg-[#e2eeed]"
                      : "border-[#e6edf1] bg-white",
                  )}
                >
                  <span
                    className={cn(
                      "text-[15px]",
                      on ? "font-bold text-[#15776d]" : "font-medium text-[#475569]",
                    )}
                  >
                    {TEAM_PERMISSION_LABELS[mod]}
                  </span>
                  {TEAM_PERMISSION_SUBLABELS[mod] ? (
                    <span className="text-xs font-medium text-[#64748b]">
                      {TEAM_PERMISSION_SUBLABELS[mod]}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setTeamPermissions((prev) => toggleTeamPermission(prev, "all"));
                setPermError(false);
              }}
              className={cn(
                "flex min-h-12 flex-col justify-center rounded-[13px] border-[1.5px] px-4",
                allPermsOn
                  ? "border-[#24a89a] bg-[#e2eeed]"
                  : "border-dashed border-[#cbd6de] bg-white",
              )}
            >
              <span
                className={cn(
                  "text-[15px]",
                  allPermsOn ? "font-bold text-[#15776d]" : "font-medium text-[#475569]",
                )}
              >
                Full Access
              </span>
            </button>
          </div>
          <div className="text-[13px] font-medium text-[#64748b]">
            Full Access can do everything an account owner can on this property. Otherwise they only see tabs you allow.
          </div>
          {permError ? (
            <div className="text-[13px] font-bold text-[#b45309]">Pick at least one</div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-0.5 flex gap-2.5">
        <button
          type="button"
          onClick={closeInvite}
          className="flex min-h-12 flex-1 items-center justify-center rounded-[13px] border-[1.5px] border-[#e6edf1] text-center text-base font-bold text-[#475569] hover:bg-[#f4f7f9]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSend}
          onClick={() => void handleSend()}
          className={cn(
            "flex min-h-12 flex-1 items-center justify-center rounded-[13px] text-center text-base font-bold",
            canSend
              ? "bg-[#24a89a] text-white hover:bg-[#1b8378]"
              : "bg-[#eef2f5] text-[#94a3b0]",
          )}
        >
          {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Send"}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="flex flex-col gap-[30px] max-sm:gap-5"
      id="access-and-team"
      data-tour="tour-settings-simple"
    >
      <h2 className="sr-only">
        Settings
      </h2>

      <div className="flex flex-col gap-3.5 max-sm:gap-2.5">
        <div className="text-[13px] font-bold tracking-[0.6px] text-[#8494a2] max-sm:text-xs">
          HOSPITABLE
        </div>
        <div className="flex items-center justify-between gap-5 rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-6 py-[22px] max-sm:gap-3.5 max-sm:rounded-2xl max-sm:px-[18px] max-sm:py-[18px]">
          <div className="flex min-w-0 items-center gap-3.5 max-sm:gap-3">
            <div
              className={cn(
                "h-2.5 w-2.5 shrink-0 rounded-full max-sm:h-[9px] max-sm:w-[9px]",
                hospConnected ? "bg-[#24a89a]" : "bg-[#cdd8e0]",
              )}
            />
            <div className="flex min-w-0 flex-col gap-[3px] max-sm:gap-0.5">
              <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720] max-sm:text-[17px]">
                Hospitable
              </div>
              <div className="text-[15px] font-medium text-[#64748b] max-sm:text-sm">
                {hospLoading ? "…" : hospStatus}
              </div>
            </div>
          </div>
          {canManage ? (
            <button
              type="button"
              onClick={() => setHospOpen(true)}
              className={cn(
                "shrink-0 rounded-xl px-5 py-[11px] text-[15px] font-bold max-sm:rounded-[11px] max-sm:px-4 max-sm:py-2.5 max-sm:text-sm",
                hospConnected
                  ? "border-[1.5px] border-[#e6edf1] text-[#475569]"
                  : "bg-[#24a89a] text-white hover:bg-[#1b8378]",
              )}
            >
              {hospConnected ? "Manage" : "Connect"}
            </button>
          ) : null}
        </div>
      </div>

      {/* Slice 21c — Buy list */}
      <div className="flex flex-col gap-[18px]">
        <div className="flex flex-col gap-1">
          <div className="text-xl font-bold tracking-[-0.3px] text-[#0f1720]">Buy list</div>
          <div className="text-sm font-medium text-[#64748b]">Amazon list for this property</div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[13px] font-bold text-[#475569]">Amazon list URL</div>
            {buyIsSaved ? (
              <div className="flex items-center gap-1.5 rounded-full bg-[#e4f5f2] px-[11px] py-[5px] text-xs font-bold text-[#17796e]">
                <Check className="h-3 w-3 stroke-[3.4]" />
                Saved
              </div>
            ) : null}
          </div>
          <input
            type="url"
            value={buyUrl}
            onChange={(e) => {
              setBuyUrl(e.target.value);
              setBuyError(false);
            }}
            placeholder="https://www.amazon.com/…"
            disabled={!canManage}
            className={cn(
              "h-[52px] w-full rounded-[14px] border-[1.5px] bg-white px-4 text-base font-medium text-[#0f1720] outline-none placeholder:text-[#94a3b0]",
              buyError ? "border-[#e0776a]" : "border-[#e6edf1] focus:border-[#24a89a]",
              !canManage && "opacity-70",
            )}
          />
          <div
            className={cn(
              "text-[13px]",
              buyError ? "font-bold text-[#c2564a]" : "font-medium text-[#8494a2]",
            )}
          >
            {buyError ? "Enter a valid link" : "List address should match this unit"}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 max-sm:flex-col max-sm:items-stretch">
          {savedBuyUrl ? (
            <a
              href={savedBuyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-[50px] items-center justify-center gap-2 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-5 text-base font-bold text-[#24a89a] hover:border-[#cfdae2] max-sm:w-full"
            >
              <ExternalLink className="h-[15px] w-[15px] stroke-[2.6]" />
              Open list
            </a>
          ) : null}
          {canManage ? (
            <button
              type="button"
              onClick={() => void saveBuyList()}
              disabled={!buyCanSave}
              className={cn(
                "inline-flex h-[50px] items-center justify-center rounded-[14px] px-6 text-base font-bold max-sm:w-full",
                buyCanSave
                  ? "bg-[#24a89a] text-white hover:bg-[#1b8378]"
                  : "bg-[#eef2f5] text-[#94a3b0]",
              )}
            >
              {updateProperty.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#e6edf1] bg-white">
        <div className="flex items-center gap-3 border-b border-[#e6edf1] px-5 py-4 max-sm:px-4 max-sm:py-3">
          <div className="flex-1 text-[12px] font-bold tracking-[0.9px] text-[#8494a2]">TEAM</div>
          {canManage ? (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="flex h-9 items-center rounded-[10px] bg-[#24a89a] px-3.5 text-[14px] font-bold text-white hover:bg-[#1b8378] max-sm:h-11 max-sm:rounded-xl max-sm:text-[15px]"
            >
              + Invite
            </button>
          ) : null}
        </div>

        {teamRows.length === 0 ? (
          <div className="flex items-center justify-center px-6 py-11">
            <div className="text-lg font-bold text-[#475569]">No team yet</div>
          </div>
        ) : (
          teamRows.map((m, i) => {
            const roleCell = m.pending ? (
              <span className="inline-flex h-6 items-center rounded-md bg-[#eef2f5] px-[9px] text-[11px] font-bold tracking-[0.8px] text-[#64748b]">
                PENDING
              </span>
            ) : m.roleLabel ? (
              <span
                className={cn(
                  "text-[14px] font-bold",
                  m.roleLabel === "Team" ? "text-[#15776d]" : "text-[#475569]",
                )}
              >
                {m.roleLabel}
              </span>
            ) : null;

            return (
              <div
                key={m.key}
                className={cn(
                  "px-5 py-4 max-sm:px-4",
                  i > 0 && "border-t border-[#eef2f5]",
                )}
              >
                <div className="flex items-center gap-3.5 max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3.5 max-sm:gap-3">
                    <div
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-[15px] font-bold",
                        m.avatarUrl
                          ? "bg-[#eef3f6]"
                          : m.pending
                            ? "border-[1.5px] border-dashed border-[#c5d0d8] bg-transparent text-[#8494a2]"
                            : "bg-[#e2eeed] text-[#15776d]",
                      )}
                    >
                      {m.avatarUrl ? (
                        <img
                          src={m.avatarUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        m.initial
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div
                        className={cn(
                          "truncate text-[15.5px] font-bold max-sm:text-[16px]",
                          m.pending ? "text-[#475569]" : "text-[#0f1720]",
                        )}
                      >
                        {m.name}
                      </div>
                      {m.email && m.email.toLowerCase() !== m.name.toLowerCase() ? (
                        <div className="truncate text-[13px] font-medium text-[#8494a2]">{m.email}</div>
                      ) : null}
                      {m.sub ? (
                        <div className="truncate text-[13px] font-medium text-[#8494a2]">{m.sub}</div>
                      ) : null}
                      {canManage && m.roleLabel === "Cleaner" && m.userId ? (
                        <div className="mt-2 max-w-[220px]">
                          <CleanerLanguageToggle
                            size="sm"
                            value={cleanerLanguages[m.userId] ?? "en"}
                            disabled={setCleanerLanguage.isPending}
                            onChange={(language) => void changeCleanerLanguage(m.userId!, language)}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="hidden shrink-0 max-sm:block">{roleCell}</div>
                  </div>
                  <div className="w-[120px] shrink-0 max-sm:hidden">{roleCell}</div>
                  {canManage ? (
                    <div className="flex shrink-0 items-center gap-2 max-sm:w-full">
                      {m.buttons.map((b) => {
                        const isSwitch = b.label === "Make Team" || b.label === "Make Cleaner";
                        const isQuiet = b.label === "Remove" || b.label === "Cancel" || b.label === "Edit";
                        return (
                          <button
                            key={b.label}
                            type="button"
                            onClick={b.onClick}
                            className={cn(
                              "flex h-[38px] items-center rounded-[10px] text-[14px] font-bold max-sm:h-[50px] max-sm:rounded-xl",
                              isSwitch &&
                                "border-[1.5px] border-[#24a89a] px-4 text-[#15776d] hover:bg-[#e2eeed] max-sm:flex-1 max-sm:justify-center max-sm:text-[16px]",
                              isQuiet &&
                                "px-3 text-[#64748b] hover:bg-[#f1f5f7] hover:text-[#0f1720] max-sm:border-[1.5px] max-sm:border-[#e6edf1] max-sm:px-3.5 max-sm:text-[15px]",
                              b.label === "Resend" &&
                                "px-3 text-[#15776d] hover:bg-[#e2eeed] max-sm:flex-1 max-sm:justify-center max-sm:border-[1.5px] max-sm:border-[#24a89a]",
                            )}
                          >
                            {b.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={hospOpen} onOpenChange={setHospOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[20px] sm:max-w-[520px]">
          <DialogTitle className="text-xl font-bold tracking-[-0.3px]">Hospitable</DialogTitle>
          <div className="pt-2">
            <HospitableConnectCard
              propertyId={propertyId}
              linkedHospitableId={property.hospitable_property_id}
              linkedPropertyName={property.name}
              onDone={() => {
                setHospOpen(false);
                void refreshHosp();
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {isMobile ? (
        <Drawer open={inviteOpen} onOpenChange={(o) => !o && closeInvite()}>
          <DrawerContent className="max-h-[92dvh] rounded-t-[24px] px-5 pb-7 pt-3">
            <div className="mx-auto mb-3 h-1.5 w-11 shrink-0 rounded-full bg-[#e6edf1]" />
            <DrawerHeader className="shrink-0 p-0 pb-2 text-left">
              <DrawerTitle className="text-[26px] font-bold tracking-[-0.7px]">Invite</DrawerTitle>
            </DrawerHeader>
            <div className="min-h-0 overflow-y-auto overscroll-contain">{inviteBody}</div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={inviteOpen} onOpenChange={(o) => !o && closeInvite()}>
          <DialogContent className="max-h-[85vh] gap-0 overflow-y-auto overscroll-contain rounded-[22px] p-7 sm:max-w-[460px]">
            <DialogTitle className="text-[26px] font-bold tracking-[-0.7px]">Invite</DialogTitle>
            <div className="pt-[22px]">{inviteBody}</div>
          </DialogContent>
        </Dialog>
      )}

      {(() => {
        const editOpen = !!editTarget;
        const editAllOn = isAllPermissionsSelected(editPermissions);
        const editCanSave = !editBusy && editPermissions.length > 0;
        const editBody = (
          <div className="flex flex-col gap-[22px]">
            {editTarget ? (
              <div className="text-[15px] font-medium text-[#64748b]">{editTarget.name}</div>
            ) : null}
            <div className="flex flex-col gap-2">
              <div className="text-[13px] font-bold text-[#64748b]">Permissions</div>
              <div className="flex flex-wrap gap-2">
                {TEAM_PERMISSION_MODULES.map((mod) => {
                  const on = editPermissions.includes(mod);
                  return (
                    <button
                      key={mod}
                      type="button"
                      onClick={() => {
                        setEditPermissions((prev) => toggleTeamPermission(prev, mod));
                        setEditPermError(false);
                      }}
                      className={cn(
                        "flex min-h-12 flex-col justify-center rounded-[13px] border-[1.5px] px-4",
                        on ? "border-[#24a89a] bg-[#e2eeed]" : "border-[#e6edf1] bg-white",
                      )}
                    >
                      <span
                        className={cn(
                          "text-[15px]",
                          on ? "font-bold text-[#15776d]" : "font-medium text-[#475569]",
                        )}
                      >
                        {TEAM_PERMISSION_LABELS[mod]}
                      </span>
                      {TEAM_PERMISSION_SUBLABELS[mod] ? (
                        <span className="text-xs font-medium text-[#64748b]">
                          {TEAM_PERMISSION_SUBLABELS[mod]}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => {
                    setEditPermissions((prev) => toggleTeamPermission(prev, "all"));
                    setEditPermError(false);
                  }}
                  className={cn(
                    "flex min-h-12 flex-col justify-center rounded-[13px] border-[1.5px] px-4",
                    editAllOn
                      ? "border-[#24a89a] bg-[#e2eeed]"
                      : "border-dashed border-[#cbd6de] bg-white",
                  )}
                >
                  <span
                    className={cn(
                      "text-[15px]",
                      editAllOn ? "font-bold text-[#15776d]" : "font-medium text-[#475569]",
                    )}
                  >
                    Full Access
                  </span>
                </button>
              </div>
              <div className="text-[13px] font-medium text-[#64748b]">
                Full Access can do everything an account owner can on this property. Otherwise they only see tabs you allow.
              </div>
              {editPermError ? (
                <div className="text-[13px] font-bold text-[#b45309]">Pick at least one</div>
              ) : null}
            </div>
            <div className="mt-0.5 flex gap-2.5">
              <button
                type="button"
                onClick={closeEditPermissions}
                className="flex min-h-12 flex-1 items-center justify-center rounded-[13px] border-[1.5px] border-[#e6edf1] text-center text-base font-bold text-[#475569] hover:bg-[#f4f7f9]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!editCanSave}
                onClick={() => void handleSaveEditPermissions()}
                className={cn(
                  "flex min-h-12 flex-1 items-center justify-center rounded-[13px] text-center text-base font-bold",
                  editCanSave
                    ? "bg-[#24a89a] text-white hover:bg-[#1b8378]"
                    : "bg-[#eef2f5] text-[#94a3b0]",
                )}
              >
                {editBusy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Save"}
              </button>
            </div>
          </div>
        );
        return isMobile ? (
          <Drawer open={editOpen} onOpenChange={(o) => !o && closeEditPermissions()}>
            <DrawerContent className="max-h-[92dvh] rounded-t-[24px] px-5 pb-7 pt-3">
              <div className="mx-auto mb-3 h-1.5 w-11 shrink-0 rounded-full bg-[#e6edf1]" />
              <DrawerHeader className="shrink-0 p-0 pb-2 text-left">
                <DrawerTitle className="text-[26px] font-bold tracking-[-0.7px]">
                  Permissions
                </DrawerTitle>
              </DrawerHeader>
              <div className="min-h-0 overflow-y-auto overscroll-contain">{editBody}</div>
            </DrawerContent>
          </Drawer>
        ) : (
          <Dialog open={editOpen} onOpenChange={(o) => !o && closeEditPermissions()}>
            <DialogContent className="max-h-[85vh] gap-0 overflow-y-auto overscroll-contain rounded-[22px] p-7 sm:max-w-[460px]">
              <DialogTitle className="text-[26px] font-bold tracking-[-0.7px]">
                Permissions
              </DialogTitle>
              <div className="pt-[22px]">{editBody}</div>
            </DialogContent>
          </Dialog>
        );
      })()}

      {isOwner ? (
        <div className="pt-4">
          <DeletePropertySection
            propertyId={propertyId}
            propertyName={property.name}
            archivedAt={property.archived_at ?? null}
          />
        </div>
      ) : null}
    </div>
  );
};

export default SettingsTab;
